import { useEffect, useId, useRef, useState } from 'react';
import type { DragEvent as ReactDragEvent, KeyboardEvent } from 'react';
import { GripVertical } from 'lucide-react';

import { t, type UiLangCode } from '../lib/i18n';
import type {
  Cents,
  Confidence,
  FieldValue,
  LineItem,
  Receipt,
} from '../lib/types';
import { formatDateForLang, parseDateForLang } from '../lib/date-format';
import { ConfidenceBadge } from './ConfidenceBadge';

interface ReceiptTableProps {
  receipt: Receipt | null;
  /** Language code used to format/parse the date field and to translate UI strings. */
  lang?: UiLangCode;
  onEdit?: (updated: Receipt) => void;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function withConfidence<T>(
  fv: FieldValue<T>,
  value: T,
  confidence: Confidence,
): FieldValue<T> {
  const next: FieldValue<T> = { ...fv, value, confidence };
  return next;
}

/**
 * Parse a decimal string ("12.34", "12", "12.5", " 12.50 ") into integer cents.
 * Returns null on garbage / negatives / NaN. Uses string math to avoid float
 * drift around the round-half boundary.
 */
function decimalToCents(str: string): number | null {
  const trimmed = str.trim();
  if (trimmed.length === 0) return null;
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function centsToDecimal(cents: Cents): string {
  return (cents / 100).toFixed(2);
}

function parseQty(str: string): number | null {
  const trimmed = str.trim();
  if (trimmed.length === 0) return null;
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function cellWrapClass(confidence: Confidence): string {
  // Yellow-amber left border on low confidence, paired with the badge.
  if (confidence === 'low') {
    return 'border-l-4 border-amber-400 bg-amber-50/40 pl-2 dark:border-amber-500 dark:bg-amber-500/10';
  }
  return 'border-l-4 border-transparent pl-2';
}

const inputBase =
  'w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-sm text-slate-900 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-400';
const readOnlyText = 'text-sm text-slate-900 dark:text-slate-100';

// ─── inline editors ──────────────────────────────────────────────────────────

interface TextEditorProps {
  value: string;
  ariaLabel: string;
  onCommit: (next: string) => void;
  inputRef?: (el: HTMLInputElement | null) => void;
  onArrowVertical?: (dir: -1 | 1) => void;
}

function TextEditor({
  value,
  ariaLabel,
  onCommit,
  inputRef,
  onArrowVertical,
}: TextEditorProps) {
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.currentTarget.value = value;
      e.currentTarget.blur();
      return;
    }
    if (onArrowVertical && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      onArrowVertical(e.key === 'ArrowDown' ? 1 : -1);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const next = e.currentTarget.value;
    if (next !== value) onCommit(next);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      key={value}
      defaultValue={value}
      aria-label={ariaLabel}
      className={inputBase}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );
}

interface MoneyEditorProps {
  cents: Cents;
  ariaLabel: string;
  onCommit: (nextCents: Cents) => void;
  onArrowVertical?: (dir: -1 | 1) => void;
}

function MoneyEditor({
  cents,
  ariaLabel,
  onCommit,
  onArrowVertical,
}: MoneyEditorProps) {
  const display = centsToDecimal(cents);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.currentTarget.value = display;
      e.currentTarget.blur();
      return;
    }
    if (onArrowVertical && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      onArrowVertical(e.key === 'ArrowDown' ? 1 : -1);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.currentTarget.value;
    const next = decimalToCents(raw);
    if (next === null) {
      // Garbage — revert to prior value, no commit.
      e.currentTarget.value = display;
      return;
    }
    if (next !== cents) onCommit(next);
    else e.currentTarget.value = display;
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      key={display}
      defaultValue={display}
      aria-label={ariaLabel}
      className={`${inputBase} text-right tabular-nums`}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );
}

interface QtyEditorProps {
  qty: number;
  ariaLabel: string;
  onCommit: (next: number) => void;
  onArrowVertical?: (dir: -1 | 1) => void;
}

function QtyEditor({
  qty,
  ariaLabel,
  onCommit,
  onArrowVertical,
}: QtyEditorProps) {
  const display = String(qty);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.currentTarget.value = display;
      e.currentTarget.blur();
      return;
    }
    if (onArrowVertical && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      onArrowVertical(e.key === 'ArrowDown' ? 1 : -1);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.currentTarget.value;
    const next = parseQty(raw);
    if (next === null) {
      e.currentTarget.value = display;
      return;
    }
    if (next !== qty) onCommit(next);
    else e.currentTarget.value = display;
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      key={display}
      defaultValue={display}
      aria-label={ariaLabel}
      className={`${inputBase} text-right tabular-nums`}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );
}

interface DateEditorProps {
  value: Date;
  lang: string;
  ariaLabel: string;
  onCommit: (next: Date) => void;
}

function DateEditor({ value, lang, ariaLabel, onCommit }: DateEditorProps) {
  // Plain text input formatted to match the receipt's locale (DD/MM/YYYY for
  // Spanish/French, MM/DD/YYYY for English). We deliberately don't use
  // <input type="date"> — the browser overrides our format with its own
  // locale, which mismatches the receipt and confuses the user.
  const display = formatDateForLang(value, lang);
  const placeholder = lang === 'eng' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.currentTarget.value = display;
      e.currentTarget.blur();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.currentTarget.value;
    const next = parseDateForLang(raw, lang);
    if (next === null) {
      e.currentTarget.value = display;
      return;
    }
    if (next.getTime() !== value.getTime()) onCommit(next);
    else e.currentTarget.value = display;
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      key={display}
      defaultValue={display}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className={inputBase}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );
}

// ─── summary row ─────────────────────────────────────────────────────────────

interface SummaryRowProps {
  label: string;
  confidence: Confidence;
  children: React.ReactNode;
}

function SummaryRow({ label, confidence, children }: SummaryRowProps) {
  return (
    <tr className="border-b border-slate-100 last:border-0 dark:border-slate-800">
      <th
        scope="row"
        className="py-2 pr-4 text-left text-sm font-medium text-slate-600 dark:text-slate-400 align-middle"
      >
        {label}
      </th>
      <td className="py-2 pr-4 align-middle">
        <div className={cellWrapClass(confidence)}>{children}</div>
      </td>
      <td className="py-2 align-middle text-sm">
        <ConfidenceBadge confidence={confidence} />
      </td>
    </tr>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export function ReceiptTable({ receipt, lang = 'eng', onEdit }: ReceiptTableProps) {
  const headingId = useId();
  const itemsHeadingId = useId();
  const editable = onEdit !== undefined;

  // Imperative refs for arrow-key item nav + autofocus on add.
  const itemNameRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [pendingFocusIdx, setPendingFocusIdx] = useState<number | null>(null);

  // Drag-and-drop reorder state. draggedIdx: which row is being dragged.
  // dragOverIdx: which row the cursor is currently over (for the insertion
  // indicator). Both clear on drop / drag-end.
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  // Restrict drag-initiation to the grip-handle column. Whole-row draggable
  // had two failure modes: clicking inputs occasionally started a drag, and
  // users couldn't tell the rest of the row was a drag handle (no cursor
  // affordance there). The handle's onMouseDown arms this ref; dragstart
  // aborts when the ref doesn't match the row trying to start. A window
  // mouseup listener clears the arm if the user clicks without dragging,
  // so a subsequent dragstart elsewhere can't ride on the stale arm.
  const dragArmedIdxRef = useRef<number | null>(null);

  useEffect(() => {
    const onWindowMouseUp = () => {
      dragArmedIdxRef.current = null;
    };
    window.addEventListener('mouseup', onWindowMouseUp);
    return () => window.removeEventListener('mouseup', onWindowMouseUp);
  }, []);

  // After a state-driven re-render that adds a row, focus its name input.
  useEffect(() => {
    if (pendingFocusIdx === null) return;
    const el = itemNameRefs.current[pendingFocusIdx];
    if (el) el.focus();
    setPendingFocusIdx(null);
  }, [pendingFocusIdx]);

  if (receipt === null) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-white px-6 py-10 text-center dark:border-slate-700 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">{t(lang, 'receipt.empty')}</p>
      </div>
    );
  }

  const currency = receipt.currency.value;

  // ─── mutators ──────────────────────────────────────────────────────────────

  const commit = (mutate: (draft: Receipt) => void): void => {
    if (!onEdit) return;
    const next = structuredClone(receipt);
    mutate(next);
    onEdit(next);
  };

  const editVendor = (v: string) =>
    commit((d) => {
      d.vendor = withConfidence(d.vendor, v, 'high');
    });

  const editDate = (v: Date) =>
    commit((d) => {
      d.date = withConfidence(d.date, v, 'high');
    });

  const editCurrency = (v: string) =>
    commit((d) => {
      d.currency = withConfidence(d.currency, v, 'high');
    });

  const editSubtotal = (v: Cents) =>
    commit((d) => {
      if (d.subtotal === undefined) return;
      d.subtotal = withConfidence(d.subtotal, v, 'high');
    });

  const editTax = (v: Cents) =>
    commit((d) => {
      if (d.tax === undefined) return;
      d.tax = withConfidence(d.tax, v, 'high');
    });

  const editTip = (v: Cents) =>
    commit((d) => {
      if (d.tip === undefined) return;
      d.tip = withConfidence(d.tip, v, 'high');
    });

  const editTotal = (v: Cents) =>
    commit((d) => {
      d.total = withConfidence(d.total, v, 'high');
    });

  const editPayment = (v: string) =>
    commit((d) => {
      if (d.paymentMethod === undefined) return;
      d.paymentMethod = withConfidence(d.paymentMethod, v, 'high');
    });

  const editItemName = (idx: number, v: string) =>
    commit((d) => {
      const it = d.items[idx];
      if (!it) return;
      it.name = withConfidence(it.name, v, 'high');
    });

  const editItemQty = (idx: number, v: number) =>
    commit((d) => {
      const it = d.items[idx];
      if (!it) return;
      it.qty = withConfidence(it.qty, v, 'high');
    });

  const editItemUnitPrice = (idx: number, v: Cents) =>
    commit((d) => {
      const it = d.items[idx];
      if (!it) return;
      if (it.unitPrice === undefined) {
        it.unitPrice = { value: v, confidence: 'high' };
      } else {
        it.unitPrice = withConfidence(it.unitPrice, v, 'high');
      }
    });

  const editItemTotal = (idx: number, v: Cents) =>
    commit((d) => {
      const it = d.items[idx];
      if (!it) return;
      it.total = withConfidence(it.total, v, 'high');
    });

  const removeItem = (idx: number) =>
    commit((d) => {
      d.items.splice(idx, 1);
    });

  const addItem = () => {
    if (!onEdit) return;
    const next = structuredClone(receipt);
    const blank: LineItem = {
      name: { value: '', confidence: 'low' },
      qty: { value: 1, confidence: 'low' },
      total: { value: 0, confidence: 'low' },
    };
    next.items.push(blank);
    onEdit(next);
    setPendingFocusIdx(next.items.length - 1);
  };

  // ─── drag-and-drop handlers ─────────────────────────────────────────────

  const handleDragStart = (e: ReactDragEvent<HTMLTableRowElement>, idx: number) => {
    // Drag may only originate from the grip handle. Ref must match the row.
    if (dragArmedIdxRef.current !== idx) {
      e.preventDefault();
      return;
    }
    setDraggedIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers need data set or they cancel the drag.
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const handleDragOver = (e: ReactDragEvent<HTMLTableRowElement>, idx: number) => {
    if (draggedIdx === null) return;
    e.preventDefault(); // allow drop
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };

  const handleDrop = (e: ReactDragEvent<HTMLTableRowElement>, targetIdx: number) => {
    e.preventDefault();
    const from = draggedIdx;
    setDraggedIdx(null);
    setDragOverIdx(null);
    dragArmedIdxRef.current = null;
    if (from === null || from === targetIdx) return;
    commit((d) => {
      const moved = d.items.splice(from, 1)[0];
      if (moved === undefined) return;
      // After splice, indices shift if from < targetIdx.
      const insertAt = from < targetIdx ? targetIdx - 1 : targetIdx;
      d.items.splice(insertAt, 0, moved);
    });
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
    setDragOverIdx(null);
    dragArmedIdxRef.current = null;
  };

  const focusItemRow = (idx: number) => {
    const el = itemNameRefs.current[idx];
    if (el) el.focus();
  };

  // Build per-item arrow-key handler that walks Up/Down through item rows.
  const itemArrow = (idx: number) => (dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= receipt.items.length) return;
    focusItemRow(target);
  };

  // Reset refs array length to match items.
  itemNameRefs.current.length = receipt.items.length;

  // ─── render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <section
        aria-labelledby={headingId}
        className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-950/50"
      >
        <h2 id={headingId} className="mb-3 text-base font-semibold text-slate-800 dark:text-slate-100">
          {t(lang, 'receipt.heading')}
        </h2>
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full table-auto">
          <tbody>
            <SummaryRow label={t(lang, 'receipt.fields.vendor')} confidence={receipt.vendor.confidence}>
              {editable ? (
                <TextEditor
                  value={receipt.vendor.value}
                  ariaLabel={t(lang, 'receipt.fields.vendor')}
                  onCommit={editVendor}
                />
              ) : (
                <span className={readOnlyText}>{receipt.vendor.value}</span>
              )}
            </SummaryRow>

            <SummaryRow label={t(lang, 'receipt.fields.date')} confidence={receipt.date.confidence}>
              {editable ? (
                <DateEditor
                  value={receipt.date.value}
                  lang={lang}
                  ariaLabel={t(lang, 'receipt.fields.date')}
                  onCommit={editDate}
                />
              ) : (
                <span className={readOnlyText}>
                  {formatDateForLang(receipt.date.value, lang)}
                </span>
              )}
            </SummaryRow>

            <SummaryRow
              label={t(lang, 'receipt.fields.currency')}
              confidence={receipt.currency.confidence}
            >
              {editable ? (
                <TextEditor
                  value={receipt.currency.value}
                  ariaLabel={t(lang, 'receipt.fields.currency')}
                  onCommit={editCurrency}
                />
              ) : (
                <span className={readOnlyText}>{receipt.currency.value}</span>
              )}
            </SummaryRow>

            {receipt.subtotal !== undefined && (
              <SummaryRow
                label={t(lang, 'receipt.fields.subtotal')}
                confidence={receipt.subtotal.confidence}
              >
                {editable ? (
                  <MoneyEditor
                    cents={receipt.subtotal.value}
                    ariaLabel={t(lang, 'receipt.fields.subtotal')}
                    onCommit={editSubtotal}
                  />
                ) : (
                  <span className={readOnlyText}>
                    {centsToDecimal(receipt.subtotal.value)} {currency}
                  </span>
                )}
              </SummaryRow>
            )}

            {receipt.tax !== undefined && (
              <SummaryRow label={t(lang, 'receipt.fields.tax')} confidence={receipt.tax.confidence}>
                {editable ? (
                  <MoneyEditor
                    cents={receipt.tax.value}
                    ariaLabel={t(lang, 'receipt.fields.tax')}
                    onCommit={editTax}
                  />
                ) : (
                  <span className={readOnlyText}>
                    {centsToDecimal(receipt.tax.value)} {currency}
                  </span>
                )}
              </SummaryRow>
            )}

            {receipt.tip !== undefined && (
              <SummaryRow label={t(lang, 'receipt.fields.tip')} confidence={receipt.tip.confidence}>
                {editable ? (
                  <MoneyEditor
                    cents={receipt.tip.value}
                    ariaLabel={t(lang, 'receipt.fields.tip')}
                    onCommit={editTip}
                  />
                ) : (
                  <span className={readOnlyText}>
                    {centsToDecimal(receipt.tip.value)} {currency}
                  </span>
                )}
              </SummaryRow>
            )}

            <SummaryRow label={t(lang, 'receipt.fields.total')} confidence={receipt.total.confidence}>
              {editable ? (
                <MoneyEditor
                  cents={receipt.total.value}
                  ariaLabel={t(lang, 'receipt.fields.total')}
                  onCommit={editTotal}
                />
              ) : (
                <span className={readOnlyText}>
                  {centsToDecimal(receipt.total.value)} {currency}
                </span>
              )}
            </SummaryRow>

            {receipt.paymentMethod !== undefined && (
              <SummaryRow
                label={t(lang, 'receipt.fields.payment')}
                confidence={receipt.paymentMethod.confidence}
              >
                {editable ? (
                  <TextEditor
                    value={receipt.paymentMethod.value}
                    ariaLabel={t(lang, 'receipt.fields.payment')}
                    onCommit={editPayment}
                  />
                ) : (
                  <span className={readOnlyText}>
                    {receipt.paymentMethod.value}
                  </span>
                )}
              </SummaryRow>
            )}
          </tbody>
        </table>
        </div>
      </section>

      <section
        aria-labelledby={itemsHeadingId}
        className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-950/50"
      >
        <h2
          id={itemsHeadingId}
          className="mb-3 text-base font-semibold text-slate-800 dark:text-slate-100"
        >
          {t(lang, 'receipt.lineItems')}
        </h2>
        {receipt.items.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t(lang, 'receipt.noItems')}</p>
        ) : (
          <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full table-auto">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-400">
                {editable && (
                  <th scope="col" className="w-6 py-2 font-medium">
                    <span className="sr-only">{t(lang, 'receipt.dragHandle')}</span>
                  </th>
                )}
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t(lang, 'receipt.cols.item')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t(lang, 'receipt.cols.qty')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t(lang, 'receipt.cols.unitPrice')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t(lang, 'receipt.cols.total')}
                </th>
                {editable && (
                  <th scope="col" className="py-2 font-medium">
                    <span className="sr-only">{t(lang, 'receipt.removeItem', { n: '' })}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {receipt.items.map((item, idx) => {
                const rowNum = idx + 1;
                const arrow = itemArrow(idx);
                const isDragged = draggedIdx === idx;
                const isDropTarget =
                  draggedIdx !== null && dragOverIdx === idx && draggedIdx !== idx;
                return (
                  <tr
                    key={idx}
                    draggable={editable}
                    onDragStart={
                      editable ? (e) => handleDragStart(e, idx) : undefined
                    }
                    onDragOver={
                      editable ? (e) => handleDragOver(e, idx) : undefined
                    }
                    onDrop={editable ? (e) => handleDrop(e, idx) : undefined}
                    onDragEnd={editable ? handleDragEnd : undefined}
                    className={`border-b border-slate-100 dark:border-slate-800 last:border-0 align-top ${
                      isDragged ? 'opacity-40' : ''
                    } ${
                      isDropTarget
                        ? 'border-t-2 border-t-indigo-500 dark:border-t-indigo-400'
                        : ''
                    }`}
                  >
                    {editable && (
                      <td
                        onMouseDown={() => {
                          dragArmedIdxRef.current = idx;
                        }}
                        className="cursor-grab select-none py-2 pl-1 pr-1 align-middle text-slate-300 hover:text-slate-600 active:cursor-grabbing dark:text-slate-600 dark:hover:text-slate-300"
                        aria-label={t(lang, 'receipt.dragHandleAria', { n: rowNum })}
                        title={t(lang, 'receipt.dragHandle')}
                      >
                        <GripVertical className="h-4 w-4" aria-hidden="true" />
                      </td>
                    )}
                    <td className="py-2 pr-4 align-middle">
                      <div className={cellWrapClass(item.name.confidence)}>
                        {editable ? (
                          <div className="flex items-center gap-2">
                            <TextEditor
                              value={item.name.value}
                              ariaLabel={`${t(lang, 'receipt.cols.item')}, ${rowNum}`}
                              onCommit={(v) => editItemName(idx, v)}
                              inputRef={(el) => {
                                itemNameRefs.current[idx] = el;
                              }}
                              onArrowVertical={arrow}
                            />
                            <ConfidenceBadge
                              confidence={item.name.confidence}
                            />
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={readOnlyText}>
                              {item.name.value}
                            </span>
                            <ConfidenceBadge
                              confidence={item.name.confidence}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-4 align-middle">
                      <div className={cellWrapClass(item.qty.confidence)}>
                        {editable ? (
                          <div className="flex items-center gap-2">
                            <QtyEditor
                              qty={item.qty.value}
                              ariaLabel={`${t(lang, 'receipt.cols.qty')}, ${rowNum}`}
                              onCommit={(v) => editItemQty(idx, v)}
                              onArrowVertical={arrow}
                            />
                            <ConfidenceBadge confidence={item.qty.confidence} />
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={readOnlyText}>
                              {item.qty.value}
                            </span>
                            <ConfidenceBadge confidence={item.qty.confidence} />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-4 align-middle">
                      <div
                        className={cellWrapClass(
                          item.unitPrice?.confidence ?? 'high',
                        )}
                      >
                        {editable ? (
                          <div className="flex items-center gap-2">
                            <MoneyEditor
                              cents={item.unitPrice?.value ?? 0}
                              ariaLabel={`${t(lang, 'receipt.cols.unitPrice')}, ${rowNum}`}
                              onCommit={(v) => editItemUnitPrice(idx, v)}
                              onArrowVertical={arrow}
                            />
                            {item.unitPrice && (
                              <ConfidenceBadge
                                confidence={item.unitPrice.confidence}
                              />
                            )}
                          </div>
                        ) : item.unitPrice !== undefined ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={readOnlyText}>
                              {centsToDecimal(item.unitPrice.value)} {currency}
                            </span>
                            <ConfidenceBadge
                              confidence={item.unitPrice.confidence}
                            />
                          </div>
                        ) : (
                          <span className="text-slate-400 dark:text-slate-600">—</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-4 align-middle">
                      <div className={cellWrapClass(item.total.confidence)}>
                        {editable ? (
                          <div className="flex items-center gap-2">
                            <MoneyEditor
                              cents={item.total.value}
                              ariaLabel={`${t(lang, 'receipt.cols.total')}, ${rowNum}`}
                              onCommit={(v) => editItemTotal(idx, v)}
                              onArrowVertical={arrow}
                            />
                            <ConfidenceBadge
                              confidence={item.total.confidence}
                            />
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={readOnlyText}>
                              {centsToDecimal(item.total.value)} {currency}
                            </span>
                            <ConfidenceBadge
                              confidence={item.total.confidence}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                    {editable && (
                      <td className="py-2 align-middle">
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          aria-label={t(lang, 'receipt.removeItem', { n: rowNum })}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 ring-1 ring-inset ring-slate-200 transition-colors hover:bg-red-50 hover:text-red-700 hover:ring-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-slate-400 dark:ring-slate-700 dark:hover:bg-red-500/10 dark:hover:text-red-400 dark:hover:ring-red-500/30"
                        >
                          <span aria-hidden="true">×</span>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {/*
               * End-of-list drop zone. The per-row top-border indicator
               * inserts BEFORE its target — without this trailing zone
               * there's no way to drop at the very end of the items
               * list. Only rendered while a line-item drag is in flight
               * to keep the table compact at rest.
               */}
              {editable && draggedIdx !== null && (
                <tr
                  onDragOver={(e) => handleDragOver(e, receipt.items.length)}
                  onDrop={(e) => handleDrop(e, receipt.items.length)}
                  aria-hidden="true"
                >
                  <td
                    colSpan={6}
                    className={`p-0 ${
                      dragOverIdx === receipt.items.length
                        ? 'h-6 rounded-md border-2 border-dashed border-indigo-500 bg-indigo-50/50 dark:border-indigo-400 dark:bg-indigo-500/10'
                        : 'h-6 border-2 border-dashed border-transparent'
                    }`}
                  />
                </tr>
              )}
            </tbody>
          </table>
          </div>
        )}
        {editable && (
          <div className="mt-3">
            <button
              type="button"
              onClick={addItem}
              className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-indigo-700 hover:shadow focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus:ring-offset-slate-950"
            >
              <span aria-hidden="true">+</span> {t(lang, 'receipt.addItem')}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
